// Time-axis helpers for the hashrate chart. The backend serves bucketed points
// (24h/7d/30d -> 5m/1h/6h) with timestamps in the user's timezone; these helpers
// turn a [start, end] window into round, correctly-positioned axis ticks and
// format tooltip times — all in the user's configured timezone, independent of
// the browser's.

export type HashrateRange = '24h' | '7d' | '30d';

export interface RangeConfig {
  // Trailing window length and the server-side bucket size, in milliseconds.
  windowMs: number;
  bucketMs: number;
}

const HOUR = 3600_000;
const DAY = 24 * HOUR;

export const RANGE_CONFIG: Record<HashrateRange, RangeConfig> = {
  '24h': { windowMs: 24 * HOUR, bucketMs: 5 * 60_000 },
  '7d': { windowMs: 7 * DAY, bucketMs: HOUR },
  '30d': { windowMs: 30 * DAY, bucketMs: 6 * HOUR },
};

export const RANGE_LABELS: Record<HashrateRange, string> = {
  '24h': '24H',
  '7d': '7D',
  '30d': '30D',
};

export const RANGES: HashrateRange[] = ['24h', '7d', '30d'];

export const GRAPH_WIDTH = 1000;

export interface AxisTick {
  fraction: number; // 0..1 position across the window
  label: string;
}

interface LocalParts {
  year: number;
  month: number; // 1..12
  day: number;
  hour: number; // 0..23
  minute: number;
}

const partsInTz = (date: Date, tz?: string): LocalParts => {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
  const out: Record<string, string> = {};

  dtf.formatToParts(date).forEach(({ type, value }) => {
    out[type] = value;
  });
  return {
    year: Number(out.year),
    month: Number(out.month),
    day: Number(out.day),
    hour: Number(out.hour) % 24, // some engines render midnight as "24"
    minute: Number(out.minute),
  };
};

// Convert a wall-clock time in `tz` to an absolute epoch-ms instant. Guess that
// the parts are UTC, then correct by the zone's offset at that guess (handles
// most DST cases; an exact fold boundary may be off by an hour, which only
// nudges a label position by a few pixels).
const localWallClockToMs = (p: LocalParts, tz?: string): number => {
  const guess = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);
  const back = partsInTz(new Date(guess), tz);
  const guessLocalAsUtc = Date.UTC(
    back.year, back.month - 1, back.day, back.hour, back.minute,
  );

  return guess - (guessLocalAsUtc - guess);
};

// Tick cadence per range: hours between ticks, or a day step.
const TICK_STEP: Record<HashrateRange, { hours?: number; days?: number }> = {
  '24h': { hours: 4 },
  '7d': { days: 1 },
  '30d': { days: 5 },
};

const pad = (n: number) => String(n).padStart(2, '0');

const formatTick = (date: Date, range: HashrateRange, tz?: string): string => {
  const p = partsInTz(date, tz);

  if (range === '24h') {
    return `${pad(p.hour)}:00`;
  }
  return `${p.month}/${p.day}`;
};

// Round, in-window ticks at sensible local boundaries (every 4h for 24h, daily
// for 7d, every 5 days for 30d), positioned by their true time fraction.
export const buildTimeTicks = (
  startMs: number,
  endMs: number,
  range: HashrateRange,
  tz?: string,
): AxisTick[] => {
  const span = endMs - startMs;

  if (span <= 0) {
    return [];
  }
  const step = TICK_STEP[range];
  const startParts = partsInTz(new Date(startMs), tz);

  // First tick at or after `start`, snapped to the cadence.
  let cursor: number;

  if (step.hours) {
    const snappedHour = Math.ceil(startParts.hour / step.hours) * step.hours;

    cursor = localWallClockToMs(
      { ...startParts, hour: snappedHour % 24, minute: 0 }, tz,
    );
    if (snappedHour >= 24) {
      cursor += DAY; // cadence rolled past midnight
    }
    while (cursor < startMs) {
      cursor += step.hours * HOUR;
    }
  } else {
    // Next local midnight at or after start.
    cursor = localWallClockToMs({ ...startParts, hour: 0, minute: 0 }, tz);
    while (cursor < startMs) {
      cursor += DAY;
    }
  }

  const ticks: AxisTick[] = [];
  const stepMs = step.hours ? step.hours * HOUR : (step.days ?? 1) * DAY;

  for (let t = cursor; t <= endMs && ticks.length < 32; t += stepMs) {
    ticks.push({
      fraction: (t - startMs) / span,
      label: formatTick(new Date(t), range, tz),
    });
  }
  return ticks;
};

// Tooltip label: a concise local date+time in the user's timezone.
export const formatBucketTime = (date: Date, tz?: string): string => (
  new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date)
);
